import { useCallback, useEffect, useState } from 'react';

import { playbackBuffers } from './playback-buffers.js';

/**
 * ★ **재생 화면의 오디오 토글** (좌상단 시계 옆 스피커 버튼).
 *
 * ── 왜 있는가 ────────────────────────────────────────────────
 *
 * **노트북으로 재생 화면을 공유하며 데모할 때** 영상 소리를 죽이고 대화를 이어가야 한다.
 * 노트북 스피커를 끄면 **화상 대화도 함께 죽으므로** 앱 안에서 그 영상만 꺼야 한다.
 *
 * **재생을 멈추지 않는다** (절대 규칙 7·14·25·31·51). 소리만 죽고 영상은 계속 간다.
 *
 * ── ★★ 왜 `muted` 가 아니라 `volume` 인가 ──────────────────────
 *
 * **`muted` 는 이미 더블 버퍼 기계의 것이다.** 어댑터 둘이 일곱 자리에서 무조건
 * 재설정한다 — 특히 `play()`(`hls-player.ts:160` · `native-hls-player.ts:209`)와
 * **`performSwap` 의 노출 순간**(`:694` · `:728`)이 `muted = false` 로 되돌린다.
 * 그래서 여기서 `muted = true` 를 걸어도 **다음 스왑 한 번에 소리가 돌아온다** —
 * 회차 전환·스킵·선택·광고 진입·광고 사이·복귀가 전부 스왑이라 **광고 pod 하나만
 * 지나가도 풀린다.**
 *
 * **`.volume` 은 저장소 전체에서 읽는 곳도 쓰는 곳도 없다.** 그래서 축이 겹치지 않는다 —
 * **기계는 `muted` 를, 사람은 `volume` 을 쓴다.** 어댑터를 한 줄도 열지 않고, 광고도 같은
 * 엘리먼트를 쓰므로 저절로 따라온다.
 *
 * 부수로 하나가 이롭다 — `muted` 를 안 건드리므로 iOS 의 **엘리먼트 단위 소리 허가**
 * (`playback-buffers.ts` 머리)와 `unlockBuffers()` 의 전제가 그대로 산다.
 *
 * ── 수명 ─────────────────────────────────────────────────────
 *
 * **데모 장치이지 제품 기능이 아니다** — 광고 On/Off(`ads/ads-enabled.ts`)와 같은 방침으로
 * **서버에도 계정에도 저장하지 않는다.** `localStorage` 한 키다.
 */
const STORAGE_KEY = 'bloom.audioMuted';

/** 같은 문서 안의 다른 컴포넌트에 알린다. `storage` 이벤트는 **다른 탭에만** 온다 */
const CHANGE_EVENT = 'bloom:audio-muted';

/**
 * ★ **없으면 「소리 있음」이다.** 광고 토글의 「없으면 켜짐」과 같은 방향의 결정이다 —
 * 저장소가 막혀 있다고(사파리 프라이빗) **데모가 조용해지는 쪽으로 실패하면** 무대에서
 * 그 사실을 아무도 모른 채 「소리가 안 난다」를 서버에서 찾게 된다.
 *
 * 그래서 **`'true'` 만 음소거다.** 값이 없거나 이상하면 소리가 난다.
 */
export function audioMuted(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setAudioMuted(next: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, next ? 'true' : 'false');
  } catch {
    // 저장하지 못해도 이 탭에서는 아래 이벤트로 바뀐다. 새로고침하면 소리로 돌아온다.
  }
  applyAudioMuted(next);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

/**
 * ★ **iOS 는 `video.volume` 설정을 무시한다** (음량이 하드웨어 전용이다). 그대로 두면
 * 아이폰에서 **버튼은 눌리는데 소리가 그대로**이고, 그것이 이 저장소가 가장 싫어하는
 * 조용한 실패다.
 *
 * ── ★★ 능력 판정 하나로는 못 가른다 (2026-09-01 실기) ────────
 *
 * **v22 는 「써 보고 읽어서」 하나로 판정했다** — `probe.volume = 0.5` 뒤에 `volume` 이
 * 1 로 남으면 못 쓰는 것이라고 봤고, 그것이 당시 아이폰의 모양이었다.
 *
 * **아이폰에서 그 전제가 깨졌다.** 지금 WebKit 은 **속성에는 쓴 값을 남기면서 출력에는
 * 반영하지 않는다** — 그래서 판정이 참이 되고 **버튼이 뜨는데 눌러도 소리가 그대로**다.
 * 사용자가 실기에서 그렇게 보고했다. **고친 뒤 같은 기기에서 버튼이 사라진 것도
 * 확인했다**(2026-09-01) — 아래 `iosFamily` 가 진짜 아이폰에서 참이라는 유일한 증거이고,
 * 하네스는 그 값을 **스스로 심으므로** 원리적으로 그것을 증명하지 못한다.
 *
 * **원리적으로 능력 검사로는 닿지 않는다** — 우리가 알고 싶은 것은 「속성이 쓰이는가」가
 * 아니라 **「출력이 따라오는가」**이고, 뒤엣것을 브라우저에서 읽을 방법이 없다. 그래서
 * **사실을 하나 더 읽는다.**
 *
 * ── 그 하나는 UA 가 아니다 ───────────────────────────────────
 *
 * **`navigator.standalone` 은 iOS 에만 있는 비표준 속성**이고(`auth/login-mode.ts` 가
 * 이미 그렇게 적었다), 여기서 보는 것은 그 **값이 아니라 있고 없음**이다 —
 *
 * | | `'standalone' in navigator` | `navigator.standalone === true` |
 * |---|---|---|
 * | 묻는 것 | **iOS 계열인가** (이 파일) | iOS **홈 화면 앱**인가 (`login-mode.ts`) |
 * | 사파리 탭 · 홈 화면 앱 · WKWebView | 셋 다 참 | 가운데만 참 |
 *
 * **둘을 섞지 말 것.** 값으로 보면 **사파리 탭의 아이폰에 버튼이 그대로 뜬다** — 지금
 * 고치는 그 증상이다. 그리고 `(display-mode: standalone)` 은 **데스크톱 PWA 도 Android
 * WebAPK 도 참**이라 여기서 쓸 수 없다(`CLAUDE.md` 의 그 줄).
 *
 * **macOS 사파리는 걸리지 않는다** — 그 속성이 없고, 거기서는 `volume` 이 실제로 듣는다.
 * `resolvePlayerKind()` 의 `nativeHlsPreferred()` 를 재사용하지 않은 이유가 그것이다:
 * 그쪽은 **사파리 계열을 통째로** 네이티브로 보내는데, 여기서 그러면 **잘 되는 버튼을
 * macOS 에서 지운다.**
 *
 * **애플이 열어 주면 이 판정은 저절로 참이 되지 않는다** — 그날 `iosFamily` 항을 지운다.
 * 그것이 능력 검사 하나였을 때와 달라진 유일한 대가이고, **알고 받는다.**
 */
export interface VolumeFacts {
  /** `video.volume` 에 써서 읽었을 때 그 값이 남는가. **출력이 따라오는가가 아니다** */
  settable: boolean;
  /** iOS 계열인가 — `navigator.standalone` 의 **유무**가 그 판정이다 */
  iosFamily: boolean;
}

/**
 * 규칙. **사실을 읽는 자리와 가른다** — `auth/login-mode.ts` 가 같은 이유로 같은 모양이다:
 * 붙어 있으면 **규칙이 틀려도 테스트가 볼 것이 없다.**
 */
export function volumeIsControllable(facts: VolumeFacts): boolean {
  return facts.settable && !facts.iosFamily;
}

/**
 * 사실을 읽는다. **DOM 과 `navigator` 를 읽는 자리는 여기 하나다.**
 *
 * 붙이지 않은 엘리먼트라 재생에 영향이 없다.
 */
export function readVolumeFacts(): VolumeFacts {
  if (typeof document === 'undefined') return { settable: false, iosFamily: false };
  let settable: boolean;
  try {
    const probe = document.createElement('video');
    probe.volume = 0.5;
    settable = probe.volume !== 1;
  } catch {
    settable = false;
  }
  const iosFamily = typeof navigator !== 'undefined' && 'standalone' in navigator;
  return { settable, iosFamily };
}

/** 한 번만 재고 캐시한다. 문서의 수명 동안 바뀌지 않는 값이다. */
let volumeControllable: boolean | null = null;

export function canControlVolume(): boolean {
  volumeControllable ??= volumeIsControllable(readVolumeFacts());
  return volumeControllable;
}

/**
 * 두 엘리먼트 **모두**에 건다. 하나만 걸면 스왑 뒤에 소리가 돌아온다 —
 * 앞뒤 버퍼가 번갈아 무대에 서기 때문이다.
 *
 * **능력 판정이 참일 때만 쓴다.** 거짓인 기기(아이폰)에는 버튼이 없어 값이 켜질 일이
 * 없지만, 조건을 여기 박아 두면 **그 값이 어떤 경로로 켜지더라도** 아이폰의 오디오
 * 경로를 건드리지 않는다.
 */
export function applyAudioMuted(muted: boolean = audioMuted()): void {
  if (!canControlVolume()) return;
  for (const el of playbackBuffers()) {
    el.volume = muted ? 0 : 1;
  }
}

/**
 * 화면이 이 값을 그린다.
 *
 * **리시버를 다시 세우지 않는다** — 값을 `ReceiverOptions` 로 주입하면 생성자 인자라
 * **토글 한 번에 영상이 처음부터 다시 시작한다**(절대 규칙 25·51). 광고 토글이 같은
 * 이유로 어댑터 안에서 읽는 것과 같은 자리다.
 */
export function useAudioMuted(): [boolean, (next: boolean) => void] {
  const [muted, setMuted] = useState(audioMuted);

  useEffect(() => {
    const sync = () => {
      setMuted(audioMuted());
    };
    window.addEventListener(CHANGE_EVENT, sync);
    // 다른 탭에서 바꾼 경우.
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const set = useCallback((next: boolean) => {
    setAudioMuted(next);
    setMuted(next);
  }, []);

  return [muted, set];
}
